// Report Permissions Dialog — iter-240 clean-style rev.
//
// One-track semantics: every report is a plain tickbox that maps to
// the user's `enabled_keys` allow-list. Nothing is visible until an
// admin ticks it on — no default-on, no visual badges, no per-key
// exceptions. Owner rows still short-circuit to the read-only notice.
//
// A `report_permissions_updated` live-sync broadcast fires when perms
// save, so the target user's sidebar + Reports directory pick up the
// new state without a manual refresh.
import React, { useEffect, useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TAB_LABELS = {
  // Iter-411 — Brand Overview sub-dashboards (Cancellation today;
  // future per-tile Brand Overview checkboxes will land here too).
  overview: "Overview",
  sales: "Sales",
  "care-team": "Care Team",
  customers: "Customers",
  accounting: "Accounting",
  marketing: "Marketing",
  admin: "Administration",
  "by-location": "By Location",
};

export default function ReportPermissionsDialog({ open, onOpenChange, user, scope }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reports, setReports] = useState([]);
  const [enabled, setEnabled] = useState(new Set());
  const [isOwnerRow, setIsOwnerRow] = useState(false);

  useEffect(() => {
    if (!open || !user || !scope) return;
    setLoading(true);
    staffApi
      .get(`/staff-users/${user.id}/report-permissions`, { params: { scope } })
      .then((r) => {
        setReports(r.data.reports || []);
        setEnabled(new Set(r.data.enabled_keys || []));
        setIsOwnerRow(!!r.data.is_owner);
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [open, user, scope]);

  const toggle = (key) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const save = async () => {
    if (!user || !scope) return;
    setSaving(true);
    try {
      await staffApi.put(
        `/staff-users/${user.id}/report-permissions`,
        { enabled_keys: [...enabled], disabled_keys: [] },
        { params: { scope } },
      );
      try {
        window.dispatchEvent(new CustomEvent("cocoon:report-permissions-updated", {
          detail: { user_id: user.id, scope },
        }));
      } catch { /* noop */ }
      toast.success(`Report permissions saved for ${user.full_name}.`);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // Group reports by tab so operators see them under the same headings
  // the Reports directory uses.
  const byTab = reports.reduce((acc, r) => {
    (acc[r.tab] = acc[r.tab] || []).push(r);
    return acc;
  }, {});
  // OVERVIEW stays pinned to the top so the Cancellation tick sits
  // above the existing report sections, as per spec (iter-411).
  const tabOrder = ["overview", "sales", "care-team", "customers", "accounting", "marketing", "admin", "by-location"]
    .filter((t) => byTab[t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="report-permissions-dialog">
        <DialogHeader>
          <DialogTitle data-testid="report-permissions-title">
            Report Permissions · {user?.full_name || ""} · {scope === "brand" ? "Brand" : "Branch"}
          </DialogTitle>
          <DialogDescription>
            Select which reports this user is authorized to view.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : isOwnerRow ? (
          <p
            className="py-6 text-center text-sm text-muted-foreground"
            data-testid="report-permissions-owner-locked"
          >
            Owner has every report enabled. This list can't be edited to keep the safety-net role intact.
          </p>
        ) : (
          <div className="max-h-[420px] overflow-y-auto space-y-4 py-2">
            {tabOrder.map((tab) => (
              <section key={tab} data-testid={`report-permissions-cat-${tab}`}>
                <h4 className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">
                  {TAB_LABELS[tab] || tab}
                </h4>
                <ul className="space-y-1.5">
                  {byTab[tab].map((r) => (
                    <li key={r.key} className="flex items-center gap-2">
                      <Checkbox
                        id={`rpp-${scope}-${r.key}`}
                        checked={enabled.has(r.key)}
                        onCheckedChange={() => toggle(r.key)}
                        data-testid={`report-permissions-check-${r.key}`}
                      />
                      <label
                        htmlFor={`rpp-${scope}-${r.key}`}
                        className="text-sm cursor-pointer select-none"
                      >
                        {r.label}
                      </label>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="report-permissions-cancel">
            Cancel
          </Button>
          {!isOwnerRow && (
            <Button onClick={save} disabled={saving || loading} data-testid="report-permissions-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
